import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MenuProduct } from '../../../models/menu-product.model';
import { CategoryService } from '../../../services/categories/categories.service';
import { Category, UnitType } from '../../../services/categories/interfaces/categories.interface';
import { MenuProductOptionsDialogComponent } from '../menu-product-options-dialog/menu-product-options-dialog.component';
import { MenuProductChannelsDialogComponent } from '../menu-product-channels-dialog/menu-product-channels-dialog.component';
import { PriceHistoryDialogComponent } from './price-history-dialog.component';

export interface EditProductDialogData {
  product: MenuProduct;
}

@Component({
  selector: 'app-editable-product-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatSelectModule, MatIconModule],
  templateUrl: './editable-product-dialog.component.html',
  styleUrls: ['./editable-product-dialog.component.scss']
})
export class EditProductDialogComponent {
  editable: MenuProduct;
  categories: Category[] = [];
  unitTypes: UnitType[] = [];
  loading = true;


  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EditProductDialogData,
    private dialogRef: MatDialogRef<EditProductDialogComponent>,
    private categoryService: CategoryService,
    private dialog: MatDialog,
  ) {
    // Clone to avoid mutating original until save
    this.editable = { ...data.product } as MenuProduct;

    // Normalize categoryUuid from multiple possible sources
    this.editable.categoryUuid =
      this.editable.categoryUuid ||
      (this.editable as any).categoryId ||
      (this.editable as any).categoryHash ||
      this.editable.category?.uuid ||
      (this.editable.category as any)?.id ||
      (data as any).categoryUuid || // Context fallback
      '';

    // Store categoryName if available
    if (!this.editable.categoryName && ((data as any).categoryName || this.editable.category?.name)) {
      (this.editable as any).categoryName = (data as any).categoryName || this.editable.category?.name;
    }

    // Normalize unitTypeUuid
    this.editable.unitTypeUuid =
      (this.editable as any).unitTypeUuid ||
      (this.editable as any).unitTypeId ||
      (this.editable as any).portionUuid ||
      (this.editable as any).unitType?.uuid ||
      (this.editable as any).unitType?.id ||
      undefined as any;

    // Load categories and unit types
    this.loadCategories();
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onSave(): void {
    if (!this.editable.name || this.editable.name.trim() === '') return;
    // Enrich payload with category and portion names/aliases for backend compatibility
    const cat = (this.categories || []).find((c: any) => (c?.uuid) === (this.editable as any).categoryUuid);
    const unit = (this.unitTypes || []).find((u: any) => (u?.uuid || u?.id) === (this.editable as any).unitTypeUuid);

    const enriched: any = { ...this.editable };
    // Coerce numeric fields
    if (enriched.sellPrice !== undefined) {
      enriched.sellPrice = Number(enriched.sellPrice);
    }
    if (enriched.costPrice !== undefined) {
      enriched.costPrice = Number(enriched.costPrice);
    }
    if (cat) {
      enriched.categoryUuid = cat.uuid;
      enriched.categoryName = cat.name;
    }
    if (unit) {
      // Send portionUuid (uuid only)
      enriched.portionUuid = unit.uuid || unit.id;
      // Clean legacy/name fields
      delete (enriched as any).unitTypeUuid;
      delete (enriched as any).unitTypeId;
      delete (enriched as any).unitTypeName;
      delete (enriched as any).portionHash;
      delete (enriched as any).portionName;
    }
    // Clean id-based category fields
    delete (enriched as any).categoryId;
    delete (enriched as any).categoryHash;
    this.dialogRef.close(enriched);
  }

  async loadCategories() {
    try {
      const cats = await this.categoryService.getCategories().toPromise();
      this.categories = Array.isArray(cats) ? cats as unknown as Category[] : [];

      console.log('EditProductDialog - Product data:', this.editable);
      console.log('EditProductDialog - Available categories:', this.categories);
      console.log('EditProductDialog - Current categoryUuid:', this.editable.categoryUuid);

      // Try to find category by UUID first
      let categoryFound = this.categories.some(c => c.uuid === this.editable.categoryUuid);

      // If categoryUuid is missing or not in list, try to match by name
      if (!categoryFound && !this.editable.categoryUuid) {
        const name = ((this.editable as any).categoryName || (this.editable as any).category?.name || '').toString();
        console.log('EditProductDialog - Trying to match by name:', name);
        if (name) {
          const found = (this.categories as any[]).find(c => c?.name === name);
          if (found) {
            console.log('EditProductDialog - Found category by name:', found);
            this.editable.categoryUuid = (found as any).uuid;
            categoryFound = true;
          }
        }
      }

      console.log('EditProductDialog - Final categoryUuid:', this.editable.categoryUuid);
      console.log('EditProductDialog - Category found:', categoryFound);

      this.onCategoryChange();
    } catch (e) {
      console.error('Error loading categories', e);
      this.categories = [];
      this.unitTypes = [];
    }
    this.loading = false;
  }

  onCategoryChange() {
    const uuid = this.editable.categoryUuid;
    const cat = this.categories.find((c: any) => c.uuid === uuid);
    // Normalize possible shapes coming from API
    const rawOptions: any[] = (cat as any)?.unitTypes || (cat as any)?.portions || (cat as any)?.categoryUnitTypes || (cat as any)?.categoryPortions || [];
    // Normalize to objects with uuid and name
    this.unitTypes = (rawOptions || []).map((u: any) => ({
      uuid: u?.uuid || u?.id,
      name: u?.name
    })) as unknown as UnitType[];
    // If current unit type is not present in the new list, try by name; else default to first
    const current = (this.editable as any).unitTypeUuid;
    const exists = this.unitTypes.some((u: any) => (u?.uuid || u?.id) === current);
    if (!exists) {
      const targetName = ((this.editable as any).unitTypeName || (this.editable as any).unitType?.name || '').toString();
      if (targetName) {
        const byName = (this.unitTypes as any[]).find(u => (u?.name || '').toString() === targetName);
        if (byName) {
          (this.editable as any).unitTypeUuid = (byName as any).uuid;
          return;
        }
      }
      if ((this.unitTypes?.length || 0) > 0) {
        (this.editable as any).unitTypeUuid = (this.unitTypes[0] as any).uuid;
      } else {
        (this.editable as any).unitTypeUuid = undefined as any;
      }
    }
  }

  openOptionsDialog(): void {
    const ref = this.dialog.open(MenuProductOptionsDialogComponent, {
      data: { product: this.editable },
      width: '92vw',
      maxWidth: '560px',
      autoFocus: false,
      restoreFocus: false,
    });
    ref.afterClosed().subscribe(() => {
      // Options are managed independently, no action needed here
    });
  }

  openChannelsDialog(): void {
    const ref = this.dialog.open(MenuProductChannelsDialogComponent, {
      data: { product: this.editable },
      width: '92vw',
      maxWidth: '520px',
      autoFocus: false,
      restoreFocus: false,
    });
    ref.afterClosed().subscribe(() => {
      // Channels are managed independently, no action needed here
    });
  }

  openPriceHistoryDialog(): void {
    const uuid = (this.editable as any).uuid;
    if (!uuid) return;
    this.dialog.open(PriceHistoryDialogComponent, {
      data: { productUuid: uuid, productName: this.editable.name },
      width: '92vw',
      maxWidth: '480px',
      autoFocus: false,
    });
  }

  // Clear helpers
  clearName() { this.editable.name = ''; }
  clearDescription() { this.editable.description = ''; }
  clearPrice() { (this.editable as any).sellPrice = undefined as any; }
}
